import type { Hero, PfpProject } from "./types";
import { pickWeighted, applyHero } from "./internals";

/**
 * Roll one selection (layerId → traitId) for the project, honoring rules:
 *  - "requires" forces a paired trait
 *  - "weightShift" multiplies weights conditionally
 *  - "incompatible" filters traits that conflict with already-picked ones
 *
 * Returns null when constraints can't be satisfied for this seed — callers
 * typically retry up to MAX_TOKEN_ATTEMPTS times before giving up.
 */
export function selectOnce(
  project: PfpProject,
  rand: () => number,
  hero?: Hero,
): Record<string, string> | null {
  const layers = [...project.layers].sort((a, b) => a.order - b.order);
  const selection: Record<string, string> = applyHero(layers, hero);

  // 1) Apply `requires` rules from already-pinned hero selections.
  let changed = true;
  while (changed) {
    changed = false;
    for (const rule of project.rules) {
      if (rule.kind !== "requires") continue;
      if (
        selection[rule.if.layerId] === rule.if.traitId &&
        selection[rule.then.layerId] !== rule.then.traitId
      ) {
        const targetLayer = layers.find((l) => l.id === rule.then.layerId);
        if (targetLayer && targetLayer.traits.some((t) => t.id === rule.then.traitId)) {
          selection[rule.then.layerId] = rule.then.traitId;
          changed = true;
        }
      }
    }
  }

  // 2) Roll the remaining layers in order, applying weightShift dynamically.
  for (const layer of layers) {
    if (selection[layer.id]) continue;
    if (layer.traits.length === 0) continue;

    let traits = layer.traits.map((t) => ({ ...t }));

    for (const rule of project.rules) {
      if (rule.kind !== "weightShift") continue;
      if (rule.on.layerId !== layer.id) continue;
      if (selection[rule.when.layerId] !== rule.when.traitId) continue;
      const idx = traits.findIndex((t) => t.id === rule.on.traitId);
      if (idx >= 0) traits[idx]!.weight = traits[idx]!.weight * rule.multiplier;
    }

    traits = traits.filter((t) => {
      for (const r of project.rules) {
        if (r.kind !== "incompatible") continue;
        const a = r.a;
        const b = r.b;
        if (
          (a.layerId === layer.id && a.traitId === t.id && selection[b.layerId] === b.traitId) ||
          (b.layerId === layer.id && b.traitId === t.id && selection[a.layerId] === a.traitId)
        ) {
          return false;
        }
      }
      return true;
    });

    if (traits.length === 0) return null;

    selection[layer.id] = pickWeighted(traits, rand).id;

    // Re-apply `requires` after this pick (cascade).
    for (const rule of project.rules) {
      if (rule.kind !== "requires") continue;
      if (
        selection[rule.if.layerId] === rule.if.traitId &&
        selection[rule.then.layerId] !== rule.then.traitId
      ) {
        const targetLayer = layers.find((l) => l.id === rule.then.layerId);
        if (targetLayer && targetLayer.traits.some((t) => t.id === rule.then.traitId)) {
          selection[rule.then.layerId] = rule.then.traitId;
        }
      }
    }
  }

  return selection;
}
